import React, { useCallback } from "react"

import { Textarea, Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import type { FormData } from "@/types/FormData"

import { AttributesInput } from "./AttributesInput"

interface AgentDetailsFormProps {
  formData: FormData
  onFormDataChange: (data: FormData) => void
}

export const AgentDetailsForm: React.FC<AgentDetailsFormProps> = ({
  formData,
  onFormDataChange,
}) => {
  const handlePromptChange = useCallback(
    (value: string) => {
      onFormDataChange({ ...formData, prompt: value })
    },
    [formData, onFormDataChange],
  )

  const handleAttributesChange = useCallback(
    (attributes: Record<string, unknown>) => {
      onFormDataChange({ ...formData, attributes })
    },
    [formData, onFormDataChange],
  )

  return (
    <div className="space-y-8 w-full">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-coval-text">
            Agent Evaluation Context
          </h4>
          <Tooltip content="Enter details about your agent's purpose, guidelines, and scope to help Coval tailor test and evaluation automation for your use case.">
            <button type="button" aria-label="Agent evaluation context info">
              <Info className="h-4 w-4 text-coval-text-muted" />
            </button>
          </Tooltip>
        </div>
        <Textarea
          aria-label="Agent evaluation context"
          placeholder="Paste your agent's prompt or describe your agent's role, domain, and intended behaviors (for Coval metric + test creation only; does NOT change agent output)."
          value={formData.prompt || ""}
          onChange={(e) => handlePromptChange(e.target.value)}
          autoComplete="off"
          rows={8}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-coval-text">
            Attributes{" "}
            <span className="text-coval-text-muted font-normal">
              - Structured Context Metadata
            </span>
          </h4>
          <Tooltip content="Attributes are structured metadata you can reference across your evaluations. Use attribute keys and values as templated variables in test set prompts, expected behaviors, and metric definitions.">
            <a
              href="https://docs.coval.ai/concepts/agents/overview#attributes"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Learn more about attributes"
            >
              <Info className="h-4 w-4 text-coval-text-muted" />
            </a>
          </Tooltip>
        </div>
        <AttributesInput
          attributes={formData.attributes}
          onChange={handleAttributesChange}
        />
      </div>
    </div>
  )
}
